import React, { useContext, useCallback, useMemo, useState, useEffect, useRef, Fragment } from 'react';
import ShowInAnimation from 'app/home/general-components/ShowInAnimation';
import { useElementSize } from 'usehooks-ts';

import { CurrentPageContext, HistoryStackContext } from 'app/home/general-components/HistoryStackContext';
import { useLocalizedStrings, LocalizationContext } from 'app/localization/Context';

import Popup, { usePopup } from 'app/home/general-components/Popup/Popup';
import { BindList } from 'app/home/main-pages/binds/Binds';
import { DeviceType, DeviceColors, AmplifierMode, SyncSettingsMode } from 'app/model/Device';

import LampOrRelayControl, { Curtain1Lamp2Control } from './device-control/LampControl';
import { PlugControl, PowerStat, WinepControl } from './device-control/PlugControl'
import { ThermostatOnOff, MarsusControl, SednaControl, EcoThermostatControl } from './device-control/Thermostat/ThermostatControl';
import SensorControl, { WifiSensorBattery } from './device-control/SensorControl';
import SirenControl, { SirenBattery } from './device-control/SirenControl';
import { RemoteDetail } from './device-control/RemoteDetail';
import { useOTAReportPage, OTAType } from './OTAReport';
import CurtainControl from './device-control/CurtainControl';
import { EditScenarioSwitchPage, ScenarioSwitchControl } from './device-control/ScenarioSwitchControl';
import { checkAndTransformDeviceForBind } from 'app/model/Bind';
import CoolerControl, { EvaporativeCoolerSettingsChart } from './device-control/CoolerControl';
import { SupportedSyncableTypes } from './sync-settings/SyncSettings';
import WCControl from './device-control/WCControl';
import DoorbellControl from './device-control/DoorbellControl';
import ElevatorControl from './device-control/ElevatorControl';

import Toggle, { ToggleHeadIconType } from 'app/home/general-components/Toggle/Toggle';
import { toEnglishNumber } from 'app/home/general-components/NumberInputHelper';
import { AudioControllerDetail, AudioControllerOnOff } from './device-control/audio-controller/AudioControllerDetail';
import { AudioControllerPlayer } from './device-control/audio-controller/AudioControllerPlayer';
import { getRelatedBindsForADevice } from 'app/model/BindData';
import CustomIconBack from 'app/home/home-components/CustomIconBack';
import { DeviceScatterViewContent, DeviceBriefLineChartViewContent } from 'app/home/main-pages/reports/InsightsAndCharts';
import { ColorConfig } from 'app/home/general-components/Charts/BriefLineChart';

import { ReactComponent as IconDownload } from 'images/general/download.svg';
import { ReactComponent as IconConnect } from 'images/general/connect.svg';
import { ReactComponent as IconDisconnect } from 'images/general/disconnect.svg';
import { ReactComponent as IconIndicate } from 'images/general/indicate.svg';
import { ReactComponent as IconDeleteWhite } from 'images/general/delete-white.svg';
import { ReactComponent as IconDeviceSettings } from 'images/setting/settings-gear.svg';
import { ReactComponent as IconDeleteRed } from 'images/general/delete-red.svg';
import { ReactComponent as IconEdit } from 'images/profile/edit.svg';
import { ReactComponent as IconMute } from 'images/general/mute-gray.svg';
import { ReactComponent as IconTick } from 'images/general/tick.svg';
import { ReactComponent as IconSettingsGears } from 'images/setting/settings-gears.svg';

import { ReactComponent as IconModeAuxDark } from 'images/device/audio-controller/mode-aux-dark.svg';
import { ReactComponent as IconModeAux } from 'images/device/audio-controller/mode-aux.svg';
import { ReactComponent as IconModeBleDark } from 'images/device/audio-controller/mode-ble-dark.svg';
import { ReactComponent as IconModeBle } from 'images/device/audio-controller/mode-ble.svg';
import { ReactComponent as IconModeHdmiDark } from 'images/device/audio-controller/mode-hdmi-dark.svg';
import { ReactComponent as IconModeHdmi } from 'images/device/audio-controller/mode-hdmi.svg';
import { ReactComponent as IconModeHinavaDark } from 'images/device/audio-controller/mode-hinava-dark.svg';
import { ReactComponent as IconModeHinava } from 'images/device/audio-controller/mode-hinava.svg';

import './DeviceDetail.scss'


function DeviceDetail({ gateway }) {
    const l = useContext(LocalizationContext);
    const s = l.strings;

    const hs = useContext(HistoryStackContext);
    const p = useContext(CurrentPageContext);

    const onBackClick = p.dismiss;

    const device = useMemo(() => {
        return gateway.devices.find(({ mac }) => mac === p.state.mac);
    }, [p, gateway.devices]);
    const [deviceCache, setDeviceCache] = useState(device);
    useEffect(() => {
        if (device === undefined) {
            onBackClick();
        } else {
            setDeviceCache(device);
        }
    }, [onBackClick, device]);

    // useEffect(() => {
    //     window.forcedChangeState = device.forcedChangeState;
    // }, [device]);
    // window.forcedChangeState({active: false})

    const relatedBinds = useMemo(() => {
        return getRelatedBindsForADevice(gateway.binds, deviceCache.mac);
    }, [deviceCache, gateway.binds]);

    const [isBackgroundRed, setBackgroundRed] = useState(false);
    const [showPowerReportPage, setShowPowerReportPage] = useState(false);

    const [sectionEditShape, setSectionEditShape] = useState(undefined);

    const negotiatedProtocol = gateway.negotiatedProtocol;
    const requestGetPower = gateway.requestGetPower;
    const requestStateChartForDevice = gateway.requestStateChartForDevice;
    const stateChartPerMac = gateway.stateChartPerMac;

    useEffect(() => {
        if (negotiatedProtocol.supportInsightsAndCharts) {
            requestStateChartForDevice({ mac: deviceCache.mac, type: deviceCache.type });
        }
    }, [requestStateChartForDevice, deviceCache.mac, deviceCache.type, negotiatedProtocol]);

    const tempValueFormatter = useCallback((v) => l.localizeNumber(`${v}°`), [l]);
    const percentValueFormatter = useCallback((v) => l.localizeNumber(`${v}%`), [l]);
    const lightLevelValueFormatter = useCallback((v) => s.lightLevels[v], [s]);

    const [pageRef, { width: pageWidth }] = useElementSize();
    const stateChartWidth = pageWidth - 16;
    const stateChart = useMemo(() => {
        if (stateChartPerMac === undefined) {
            return undefined;
        }
        return stateChartPerMac[deviceCache.mac]
    }, [stateChartPerMac, deviceCache.mac]);
    const stateChartViews = useMemo(() => {
        const views = [];
        if (stateChart === undefined)
            return views;
        switch (deviceCache.type) {
            case DeviceType.multipurposeSensor:
            case DeviceType.motion:
            case DeviceType.wifiMotionSensor:
            case DeviceType.doorSensor:
            case DeviceType.wifiDoorSensor:
            case DeviceType.evaporativeCooler:
            case DeviceType.wc:
            case DeviceType.curtain:
            case DeviceType.plug:
            case DeviceType.relay4:
            case DeviceType.lamp:
            case DeviceType.lampX1TwoWay:
            case DeviceType.dpst:
            case DeviceType.lampX3:
            case DeviceType.curtain1Lamp2:
            case DeviceType.lampX4:
                views.push(
                    <DeviceScatterViewContent
                        l={l} chartWidth={stateChartWidth}
                        device={{ mac: deviceCache.mac, type: deviceCache.type, num_channels: deviceCache.num_channels }}
                        stateChart={stateChart}
                        key={"device-state-chart-scatter-" + deviceCache.mac} />
                );
                break;
            default:
                break;
        }
        switch (deviceCache.type) {
            case DeviceType.multipurposeSensor:
                if (stateChart["temperature"] !== undefined)
                    views.push(
                        <DeviceBriefLineChartViewContent
                            l={l} chartWidth={stateChartWidth} stateChart={stateChart}
                            chartKey={"chart-device-state-chart-for-temperature-" + deviceCache.mac}
                            key={"device-state-chart-for-temperature-" + deviceCache.mac}
                            lineConfigs={[{
                                stateKey: "temperature",
                                colorConfig: ColorConfig.greenWithGrad,
                                labelName: "envTemperature",
                            }]} valueFormatter={tempValueFormatter} />
                    );
                if (stateChart["humidity"] !== undefined)
                    views.push(
                        <DeviceBriefLineChartViewContent
                            l={l} chartWidth={stateChartWidth} stateChart={stateChart}
                            chartKey={"chart-device-state-chart-for-humidity-" + deviceCache.mac}
                            key={"device-state-chart-for-humidity-" + deviceCache.mac}
                            lineConfigs={[{
                                stateKey: "humidity",
                                colorConfig: ColorConfig.blue2WithGrad,
                                labelName: "envHumidity",
                            }]} valueFormatter={percentValueFormatter} />
                    );
                if (stateChart["light_level"] !== undefined)
                    views.push(
                        <DeviceBriefLineChartViewContent
                            l={l} chartWidth={stateChartWidth} stateChart={stateChart}
                            chartKey={"chart-device-state-chart-for-light_level-" + deviceCache.mac}
                            key={"device-state-chart-for-light_level-" + deviceCache.mac}
                            lineConfigs={[{
                                stateKey: "light_level",
                                colorConfig: ColorConfig.yellowWithGrad,
                                labelName: "envLight",
                            }]} valueFormatter={lightLevelValueFormatter} marginLeft={60} />
                    );
                break;
            case DeviceType.hinavaEcoThermostat:
                if ((stateChart["set_point"] !== undefined) && (stateChart["env_temperature"] !== undefined))
                    views.push(
                        <DeviceBriefLineChartViewContent
                            l={l} chartWidth={stateChartWidth} stateChart={stateChart}
                            chartKey={"chart-device-state-chart-for-" + deviceCache.mac}
                            chart={"device-state-chart-for-" + deviceCache.mac}
                            lineConfigs={[{
                                stateKey: "set_point",
                                colorConfig: ColorConfig.yellow,
                                labelName: "setPoint",
                            },
                            {
                                stateKey: "env_temperature",
                                colorConfig: ColorConfig.blueWithGrad,
                                labelName: "envTemperature",
                            }]} valueFormatter={tempValueFormatter} />
                    );
                break;
            default:
                break;
        }
        return views;
    }, [l, stateChartWidth, deviceCache.mac, deviceCache.type, deviceCache.num_channels, stateChart,
        lightLevelValueFormatter, percentValueFormatter, tempValueFormatter]);
    const supportStateChart = stateChartViews.length > 0;

    const canHaveBind = useMemo(() => {
        const canBeBindSource = checkAndTransformDeviceForBind(negotiatedProtocol, "if", deviceCache) !== null;
        const canBeBindDest = checkAndTransformDeviceForBind(negotiatedProtocol, "then", deviceCache) !== null;
        return canBeBindSource || canBeBindDest;
    }, [deviceCache, negotiatedProtocol]);

    const detailedData = useMemo(() => {
        switch (deviceCache.type) {
            case DeviceType.plug:
                return {
                    content: <PlugControl device={deviceCache} />,
                    controlClassName: "control",
                    showDCPanel: true,
                    additionalContent: <PowerStat
                        requestGetPower={requestGetPower} device={deviceCache}
                        showPowerReportPage={showPowerReportPage}
                        setShowPowerReportPage={setShowPowerReportPage} />
                }
            case DeviceType.winep:
                return {
                    content: <WinepControl device={deviceCache} />,
                    controlClassName: "winep-control",
                    showDCPanel: true,
                    additionalContent: <PowerStat
                        requestGetPower={requestGetPower} device={deviceCache}
                        showPowerReportPage={showPowerReportPage}
                        setShowPowerReportPage={setShowPowerReportPage} />
                }
            case DeviceType.doorbell:
                return {
                    content: <DoorbellControl device={deviceCache} />,
                    controlClassName: "control",
                    showDCPanel: true,
                    additionalContent: null
                }
            case DeviceType.elevator1:
                return {
                    content: <ElevatorControl device={deviceCache} />,
                    controlClassName: "control",
                    showDCPanel: true,
                    additionalContent: null
                }
            case DeviceType.curtain:
                return {
                    content: <CurtainControl device={deviceCache} />,
                    controlClassName: "control",
                    showDCPanel: true,
                    additionalContent: null
                }
            case DeviceType.curtain1Lamp2:
                return {
                    content: <Curtain1Lamp2Control device={deviceCache} />,
                    controlClassName: "control",
                    showDCPanel: true,
                    additionalContent: null
                }
            case DeviceType.lamp:
            case DeviceType.lampX1TwoWay:
            case DeviceType.dpst:
            case DeviceType.lampX3:
            case DeviceType.lampX4:
            case DeviceType.relay4:
                return {
                    content: <LampOrRelayControl device={deviceCache} />,
                    controlClassName: "control",
                    showDCPanel: true,
                    additionalContent: null
                }
            case DeviceType.evaporativeCooler:
                return {
                    content: <CoolerControl device={deviceCache} />,
                    controlClassName: "control",
                    showDCPanel: true,
                    additionalContent: null
                }
            case DeviceType.wc:
                return {
                    content: <WCControl device={deviceCache} />,
                    controlClassName: "control",
                    showDCPanel: true,
                    additionalContent: null
                }
            case DeviceType.scenarioSwitch:
                return {
                    content: <ScenarioSwitchControl
                        device={deviceCache}
                        setSectionEditShape={setSectionEditShape} />,
                    controlClassName: "scenario-switch-detail-control",
                    showDCPanel: false,
                    additionalContent: (
                        <DeviceDetailSecondPageAnimator isActive={sectionEditShape !== undefined}>
                            <EditScenarioSwitchPage
                                scenarios={gateway.scenarios}
                                device={deviceCache}
                                sectionEditShape={sectionEditShape}
                                setSectionEditShape={setSectionEditShape} />
                        </DeviceDetailSecondPageAnimator>)
                }
            case DeviceType.motion:
            case DeviceType.doorSensor:
                return {
                    content: <SensorControl device={deviceCache} />,
                    controlClassName: "sensor-control",
                    showDCPanel: true,
                    additionalContent: null
                }
            case DeviceType.wifiMotionSensor:
            case DeviceType.wifiDoorSensor:
            case DeviceType.multipurposeSensor:
                return {
                    content: <SensorControl device={deviceCache} />,
                    controlClassName: "sensor-control",
                    showDCPanel: true,
                    additionalContent: null,
                    batteryComponent: <WifiSensorBattery device={deviceCache} />,
                }
            case DeviceType.amplifier:
                return {
                    content:
                        <AudioControllerDetail
                            device={deviceCache}
                            currentUser={gateway.currentUser} />,
                    controlClassName: "audio-controller-detail",
                    showDCPanel: false,
                    additionalContent: <Fragment>
                        <AudioControllerOnOff device={deviceCache} />
                        <AudioControllerPlayer device={deviceCache} />
                    </Fragment>
                }
            case DeviceType.sedna:
                return {
                    content: <SednaControl device={deviceCache} />,
                    controlClassName: "thermostat-control",
                    showDCPanel: false,
                    additionalContent: <ThermostatOnOff device={deviceCache} />
                }
            case DeviceType.hinavaEcoThermostat:
                return {
                    content: <EcoThermostatControl device={deviceCache} />,
                    controlClassName: "thermostat-control",
                    showDCPanel: false,
                    additionalContent: <ThermostatOnOff device={deviceCache} />
                }
            case DeviceType.marsus:
                return {
                    content: <MarsusControl device={deviceCache} />,
                    controlClassName: "thermostat-control",
                    showDCPanel: false,
                    additionalContent: <ThermostatOnOff device={deviceCache} />
                }
            case DeviceType.siren:
                return {
                    content: <SirenControl device={deviceCache} setBackgroundRed={setBackgroundRed} />,
                    controlClassName: "control",
                    showDCPanel: true,
                    batteryComponent: <SirenBattery device={deviceCache} />,
                    additionalContent: null,
                }
            case DeviceType.remote:
            case DeviceType.tuyaRemote:
                return {
                    content: <RemoteDetail
                        negotiatedProtocol={negotiatedProtocol}
                        device={deviceCache} gateway={gateway}
                        dismiss={onBackClick}
                    />,
                    controlClassName: "remote-detail",
                    showDCPanel: false,
                    batteryComponent: null,
                    additionalContent: null,
                }
            default:
                return {
                    content: null,
                    controlClassName: "control",
                    showDCPanel: true,
                    additionalContent: null
                }
        }
    }, [deviceCache, onBackClick, gateway, negotiatedProtocol,
        showPowerReportPage, requestGetPower, sectionEditShape]);

    const indicateButtonRef = useRef(null);
    const [indicateFlashing, setIndicateFlashing] = useState(false);
    const onIndicateClick = useCallback(() => {
        if (!indicateFlashing) {
            deviceCache.indicate();
            indicateButtonRef.current.className = "indicate-container flashing";
            setTimeout(() => {
                try {
                    setIndicateFlashing(false);
                    indicateButtonRef.current.className = "indicate-container";
                }
                catch (err) {
                    console.log(err);
                }
            }, 1500 * 7 + 100); // indicate-time
            setIndicateFlashing(true);
        }
    }, [deviceCache, indicateFlashing]);

    // sedna has no indicate functionality
    const showIndicateButton = (gateway.currentUser.permissions.device.change_state) && (gateway.negotiatedProtocol.hasDeviceSetting === true) && (deviceCache.type !== DeviceType.sedna);

    const [openRemovePopup, removeDevicePopup] = useRemoveDevicePopup(deviceCache, onBackClick)

    // const [deviceSettingPageActive, setDeviceSettingPageActive] = useState(false);

    const showRedDot = (deviceCache.active === true) && (deviceCache.ota_exists === true) && (gateway.currentUser.permissions.setting); // && hasDeviceSetting, in action

    const headerGroup = useMemo(() => {
        if (deviceCache.type === DeviceType.amplifier) {
            return (
                <div className="header-group">
                    <CustomIconBack className="back-icon" />
                    <div />
                    {
                        gateway.negotiatedProtocol.hasDeviceSetting ? (
                            <div className="button-border" onClick={() => hs.push("/devices/detail/audio-controller-manage", { mac: deviceCache.mac })}>
                                <div className="label">{s.audioController.manage}</div>
                                <IconDeviceSettings className="icon" />
                                {showRedDot ?
                                    <div className="red-dot" /> : null}
                            </div>
                        ) : null
                    }
                    {
                        gateway.negotiatedProtocol.hasDeviceSetting && gateway.currentUser.permissions.setting ? (
                            <IconDeleteWhite className="remove-icon" onClick={openRemovePopup} />
                        ) : null
                    }
                </div>
            );
        } else {
            return (
                <div className="header-group">
                    <CustomIconBack className="back-icon" />
                    <div />
                    {
                        gateway.negotiatedProtocol.hasDeviceSetting && gateway.currentUser.permissions.setting ? (
                            <Fragment>
                                <div className="button-border" onClick={() => { hs.push("/devices/detail/settings", { mac: deviceCache.mac }) }}>
                                    <div className="label">{s.deviceSettings.button}</div>
                                    <IconDeviceSettings className="icon" />
                                    {showRedDot ?
                                        <div className="red-dot" /> : null}
                                </div>
                                <IconDeleteWhite className="remove-icon" onClick={openRemovePopup} />
                            </Fragment>
                        ) : null
                    }
                </div>
            );
        }
    }, [openRemovePopup, hs,
        gateway.negotiatedProtocol.hasDeviceSetting,
        gateway.currentUser, showRedDot,
        deviceCache.type, deviceCache.mac, s.audioController.manage, s.deviceSettings.button]);

    const showMuteIcon = (deviceCache.type === DeviceType.scenarioSwitch) && (!deviceCache.result_sound);

    const alwaysShowPageTitleWhite = (deviceCache.type === DeviceType.amplifier);

    if (deviceCache.active) {
        return ( // Connected
            <div className="device-detail-page" ref={pageRef}>
                <div className="bg-blank" />
                <div className="bg-secondary-color" />

                <div className="bg-blank" style={{ zIndex: -9, opacity: isBackgroundRed ? 1 : 0, transition: "opacity 0.5s linear" }} />
                <div className="bg-alarm-color" style={{ zIndex: -8, opacity: isBackgroundRed ? 1 : 0, transition: "opacity 0.5s linear" }} />


                <div className="back-button" onClick={onBackClick} />

                <div className="device-detail-page-content">
                    {headerGroup}

                    <div className="name-group">
                        <div className="label">{deviceCache.name}</div>
                        {showMuteIcon ? <IconMute className="mute-icon" /> : null}
                        {showIndicateButton ? (
                            <div className="indicate-container" ref={indicateButtonRef} onClick={onIndicateClick}>
                                <IconIndicate className="icon" onClick={onIndicateClick} />
                            </div>
                        ) : null}
                    </div>

                    <div className="connection">
                        <div className="label connection-label device-detail-element-transparent">{s.deviceConnected}</div>
                        <IconConnect className="icon" />
                        {detailedData.batteryComponent}
                    </div>

                    <div className={detailedData.controlClassName}>
                        {detailedData.content}
                    </div>
                    {detailedData.additionalContent}

                    {
                        supportStateChart ?
                            <Fragment>
                                <div className="label with-margin-bottom-8">
                                    {s.reportsOfThisDevice.label}
                                    <div className="smaller">{s.reportsOfThisDevice.smallerLabel}</div>
                                </div>
                                <div className="state-chart">
                                    {stateChartViews}
                                </div>
                            </Fragment>
                            : null
                    }

                    {
                        canHaveBind ? <Fragment>
                            <div className="label">{s.bindsOfThisDevice}</div>
                            <BindList
                                style={{ overflow: "unset" }}
                                cantChangeEnable={!gateway.currentUser.permissions.bind.change_enable}
                                devices={gateway.devices}
                                scenarios={gateway.scenarios}
                                noBindText={s.noBindForThisDevice}
                                binds={relatedBinds}
                                transparent={relatedBinds.length === 0}
                                cannotShowDetail={true} />
                        </Fragment> : null
                    }

                    {removeDevicePopup}
                </div>
            </div>
        )
    } else { // Disconnected
        return (
            <div className="device-detail-page" ref={pageRef}>
                <div className="bg-blank" />
                <div className="bg-disconnect-color" />
                <div className="back-button" onClick={onBackClick} />

                <div className="device-detail-page-content">
                    {headerGroup}

                    <div className={alwaysShowPageTitleWhite ? "name-group" : "name-group-deactive"}>
                        <div className="label">{deviceCache.name}</div>
                        {showMuteIcon ? <IconMute className="mute-icon" /> : null}
                        {/* {showIndicateButton ? (
                            <div className="indicate-container">
                                <IconIndicate className="icon" />
                            </div>
                        ) : null} */}
                    </div>

                    <div />

                    <div className={detailedData.controlClassName}>
                        {detailedData.content}
                        {detailedData.showDCPanel ?
                            <div className="device-detail-dc-panel"
                                dir={l.isRTL ? "rtl" : "ltr"}
                                lang={l.language}>
                                <div>
                                    <div />
                                    <div className="label connection-label">{s.deviceDisonnected}</div>
                                    <IconDisconnect className="icon" />
                                    <div />
                                </div>
                            </div> : null}
                    </div>
                    {detailedData.additionalContent}

                    {
                        supportStateChart ?
                            <Fragment>
                                <div className="label with-margin-bottom-8 device-detail-element-transparent">
                                    {s.reportsOfThisDevice.label}
                                    <div className="smaller">{s.reportsOfThisDevice.smallerLabel}</div>
                                </div>
                                <div className="state-chart device-detail-element-transparent">
                                    {stateChartViews}
                                </div>
                            </Fragment>
                            : null
                    }

                    {
                        canHaveBind ? <Fragment>
                            <div className="label device-detail-element-transparent">{s.bindsOfThisDevice}</div>
                            <BindList
                                style={{ overflow: "unset" }}
                                cantChangeEnable={!gateway.currentUser.permissions.bind.change_enable}
                                devices={gateway.devices}
                                scenarios={gateway.scenarios}
                                noBindText={s.noBindForThisDevice}
                                binds={relatedBinds}
                                transparent={true}
                                cannotShowDetail={true} />
                        </Fragment> : null
                    }

                    {removeDevicePopup}
                </div>
            </div>
        )
    }
}

const animationPageVariantsRTL = {
    in: "translateX(-100%)",
    out: "translateX(-200%)",
};
const animationPageVariantsLTR = {
    in: "translateX(-100%)",
    out: "translateX(0%)",
};

export function DeviceDetailSecondPageAnimator({ isActive, children }) {
    const l = useContext(LocalizationContext);

    return (
        <ShowInAnimation
            show={isActive}
            transformVariants={l.isRTL ? animationPageVariantsRTL : animationPageVariantsLTR}
            duration={0.5}
            ease="ease-in-out"
            className="device-detail-rename-device-page">
            {children}
        </ShowInAnimation>
    );
}

export function DeviceSettingPageInner({ negotiatedProtocol, devices, otaProgressController }) {
    const hs = useContext(HistoryStackContext);
    const p = useContext(CurrentPageContext);
    const dismiss = p.dismiss;

    const device = useMemo(() => {
        for (let i = 0; i < devices.length; i++) {
            const d = devices[i];
            if (d.mac === p.state.mac) {
                return d
            }
            if (d.sub_devices) {
                for (let j = 0; j < d.sub_devices.length; j++) {
                    const d2 = d.sub_devices[j];
                    if (d2.mac === p.state.mac) {
                        return d2
                    }
                }
            }
        }
        return devices.find(({ mac }) => mac === p.state.mac);
    }, [p, devices]);

    const s = useLocalizedStrings();
    const ds = s.deviceSettings;

    const showColorSettings =
        (device.color !== undefined)
        && (device.active === true)
        && ((device.type === DeviceType.curtain) ||
            (device.type === DeviceType.multipurposeSensor) ||
            (device.type === DeviceType.wifiMotionSensor) ||
            (device.type === DeviceType.hinavaEcoThermostat) ||
            (device.type === DeviceType.evaporativeCooler) ||
            (device.type === DeviceType.wc) ||
            (device.type === DeviceType.doorbell) ||
            (device.type === DeviceType.elevator1) ||
            (device.type === DeviceType.curtain1Lamp2) ||
            (device.type === DeviceType.lamp) ||
            (device.type === DeviceType.lampX1TwoWay) ||
            (device.type === DeviceType.dpst) ||
            (device.type === DeviceType.lampX3) ||
            (device.type === DeviceType.lampX4));

    const canUseRoomColor =
        (negotiatedProtocol.hasRoom) &&
        (device.roomName !== undefined);

    const [selectedColorCode, _setColorCode] = useState(device.color);
    const setColorCode = useCallback((code) => {
        _setColorCode(code);
        device.changeState({ color: code });
    }, [device]);

    useEffect(() => {
        _setColorCode(device.color);
    }, [device.color]);

    const connected = (device.active !== false);
    // این قبلا === ترو بود. بخاطر ستینگ ریموت مجازی این کارو کردم. نمیدونم مشکل دیگه درست میکنه یا نه

    const showVersionInfo = (device.ota_exists !== undefined) && (device.ota_exists !== null) && connected;
    const deviceCanUpdate = (device.ota_exists === true) && (negotiatedProtocol.canOTADeviceFromClient);
    const startOTA = useOTAReportPage(otaProgressController, OTAType.device, device.startOTA);

    let lampNamesCount = 0;
    if (device.use_custom_switch_names !== undefined) {
        switch (device.type) {
            case DeviceType.lamp:
            case DeviceType.lampX1TwoWay:
                lampNamesCount = 1;
                break;
            case DeviceType.dpst:
            case DeviceType.curtain1Lamp2:
                lampNamesCount = 2;
                break;
            case DeviceType.lampX3:
                lampNamesCount = 3;
                break;
            case DeviceType.lampX4:
            case DeviceType.relay4:
                lampNamesCount = 4;
                break;
            default:
                break;
        }
    }

    const showCurtainOneTime = (device.type === DeviceType.curtain) && (device.num_channels === 1) && (device.channel1_time !== undefined);
    const showCurtainTwoTime = (device.type === DeviceType.curtain) && (device.num_channels === 2) && (device.channel1_time !== undefined) && (device.channel2_time !== undefined);

    const isSyncSettingsSupported =
        (negotiatedProtocol.hasSyncSettings) &&
        SupportedSyncableTypes.includes(device.type);

    let syncSettingsMode = "none";
    if (device.sync_settings !== undefined) {
        if (device.sync_settings.mode === SyncSettingsMode.main) {
            syncSettingsMode = "main";
        } else if (device.sync_settings.mode === SyncSettingsMode.follower) {
            syncSettingsMode = "follower";
        }
    }

    return (
        <div className="device-detail-page">
            <div className="bg-blank" />
            <div className={connected ? "bg-secondary-color" : "bg-disconnect-color"} />
            <div className="add-device-page-content device-setting-page-content">
                <CustomIconBack className="nav-icon" /> <div className="nav-button" onClick={dismiss} />

                <div className="header">{ds.button}</div>

                {/* NAME */}
                <AskText header={ds.name} originalText={device.name} sendText={device.rename} />

                {/* Doorbell time */}
                {
                    (device.type !== DeviceType.doorbell) ? undefined : (
                        <AskSeconds header={ds.ringDuration}
                            supportDot={true}
                            originalValue={device.duration / 1000}
                            sendValue={(i) => { device.changeState({ duration: Math.round(i * 1000) }); }}
                            minValue={0.2} maxValue={60} />
                    )
                }

                {/* Elevator led duration */}
                {
                    (device.type !== DeviceType.elevator1) ? undefined : (
                        <AskSeconds header={ds.elevatorLEDDuration}
                            originalValue={device.led_duration}
                            sendValue={(i) => { device.changeState({ led_duration: i }); }}
                            minValue={1} maxValue={120} />
                    )
                }

                {/* Curtain time */}
                {
                    (device.type !== DeviceType.curtain1Lamp2) ? undefined : (
                        <AskSeconds header={ds.curtainTime}
                            originalValue={device.curtain_time}
                            sendValue={(i) => { device.changeState({ curtain_time: i }); }}
                            minValue={1} maxValue={999} />
                    )
                }
                {
                    !showCurtainOneTime ? undefined : (
                        <AskSeconds header={ds.curtainTime}
                            originalValue={device.channel1_time}
                            sendValue={(i) => { device.changeState({ channel1_time: i }); }}
                            minValue={1} maxValue={999} />
                    )
                }
                {
                    !showCurtainTwoTime ? undefined : <Fragment>
                        <AskSeconds header={ds.curtainTime1}
                            originalValue={device.channel1_time}
                            sendValue={(i) => { device.changeState({ channel1_time: i }); }}
                            minValue={1} maxValue={999} />
                        <AskSeconds header={ds.curtainTime2}
                            originalValue={device.channel2_time}
                            sendValue={(i) => { device.changeState({ channel2_time: i }); }}
                            minValue={1} maxValue={999} />
                    </Fragment>
                }

                {/* MultipurposeSensor */}
                {((device.type === DeviceType.multipurposeSensor) && (device.active)) ?
                    <AskDegree header={ds.envTemperatureCalibrationOffset}
                        supportNegativeNumber={true} supportDot={true}
                        originalValue={device.temperature_calibration_offset}
                        sendValue={(i) => { device.changeState({ temperature_calibration_offset: i }) }}
                        minValue={-10} maxValue={10} /> : null}

                {/* HinavaEcoThermostat */}
                {
                    ((device.type === DeviceType.hinavaEcoThermostat) && (device.active)) ? <Fragment>
                        <AskDegree header={ds.envTemperatureCalibrationOffset}
                            supportNegativeNumber={true} supportDot={true}
                            originalValue={device.env_temperature_calibration_offset}
                            sendValue={(i) => { device.changeState({ env_temperature_calibration_offset: i }); }}
                            minValue={-10} maxValue={10} />
                        <AskDegree header={ds.turboSetPointOffset}
                            supportNegativeNumber={false} supportDot={false}
                            originalValue={device.turbo_set_point_offset}
                            sendValue={(i) => { device.changeState({ turbo_set_point_offset: i }); }}
                            minValue={1} maxValue={9} />
                        <AskMinutes header={ds.turboTimer}
                            originalValue={device.turbo_timer}
                            sendValue={(i) => { device.changeState({ turbo_timer: i }); }}
                            minValue={5} maxValue={600} />
                        <AskOnOff
                            show={true}
                            marginTop={16} header={ds.keepOnAfterRebootH} info={ds.keepOnAfterReboot}
                            originalState={device.keep_on_after_reboot ? true : false}
                            sendState={(s) => { device.changeState({ keep_on_after_reboot: s }); }} />
                        <AskOnOff
                            show={true}
                            marginTop={16} header={ds.showTemperatureWhenIsOffH} info={ds.showTemperatureWhenIsOff}
                            originalState={device.show_temperature_when_is_off ? true : false}
                            sendState={(s) => { device.changeState({ show_temperature_when_is_off: s }); }} />
                    </Fragment> : null
                }

                {/* WifiDoorSensor + wifiMotionSensor critical_battery_level */}
                {
                    (((device.type === DeviceType.multipurposeSensor) ||
                        (device.type === DeviceType.wifiDoorSensor) ||
                        (device.type === DeviceType.wifiMotionSensor)) && (device.active)) ? (
                        <AskPercentage header={ds.criticalBatteryLevel}
                            originalValue={device.critical_battery_level}
                            sendValue={(i) => { device.changeState({ critical_battery_level: i }); }}
                            minValue={0} maxValue={100} />
                    ) : null
                }

                {/* wifiMotionSensor motion_detection_duration */}
                <AskOnOff
                    show={(device.type === DeviceType.wifiMotionSensor) &&
                        (device.active) &&
                        (device.motion_detection_duration !== undefined)}
                    marginTop={24} header={ds.motionSensorSensitivity} info={ds.motionSensorSensitivityInfo}
                    originalState={device.motion_detection_duration === 0 ? true : false}
                    sendState={(s) => { device.changeState({ motion_detection_duration: s ? 0 : 60 }); }} />

                {/* Scenario Switch: sound setting */}
                <AskOnOff
                    show={(device.type === DeviceType.scenarioSwitch) && (device.active)}
                    marginTop={24} header={ds.sound} info={ds.soundInfo}
                    originalState={device.result_sound ? true : false}
                    sendState={(s) => { device.changeState({ result_sound: s }); }} />

                {/* Lamp Switch Names */}
                {
                    lampNamesCount > 0 ? <Fragment>
                        <AskOnOff
                            show={true}
                            marginTop={24} header={ds.switchNames} info={ds.switchNamesInfo}
                            originalState={device.use_custom_switch_names ? true : false}
                            sendState={(s) => { device.changeState({ use_custom_switch_names: s }); }} />

                        {device.use_custom_switch_names ? (
                            <div className="inner-scope">
                                <AskText header={ds.switchName1} marginTop={0}
                                    originalText={device.switch1_name !== undefined ?
                                        device.switch1_name : ds.switchName1Default}
                                    sendText={(s) => { device.changeState({ switch1_name: s }); }} />
                                {
                                    (lampNamesCount >= 2) ? (
                                        <AskText header={ds.switchName2}
                                            originalText={device.switch2_name !== undefined ?
                                                device.switch2_name : ds.switchName2Default}
                                            sendText={(s) => { device.changeState({ switch2_name: s }); }} />
                                    ) : null
                                }
                                {
                                    (lampNamesCount >= 3) ? (
                                        <AskText header={ds.switchName3}
                                            originalText={device.switch3_name !== undefined ?
                                                device.switch3_name : ds.switchName3Default}
                                            sendText={(s) => { device.changeState({ switch3_name: s }); }} />
                                    ) : null
                                }
                                {
                                    (lampNamesCount >= 4) ? (
                                        <AskText header={ds.switchName4}
                                            originalText={device.switch4_name !== undefined ?
                                                device.switch4_name : ds.switchName4Default}
                                            sendText={(s) => { device.changeState({ switch4_name: s }); }} />
                                    ) : null
                                }
                            </div>
                        ) : null}
                    </Fragment> : null
                }

                {/* WC Settings */}
                {
                    ((device.type === DeviceType.wc) && (device.active)) ? <Fragment>
                        <AskSeconds header={ds.refreshDuration}
                            originalValue={device.refresh_scenario_duration}
                            sendValue={(i) => { device.changeState({ refresh_scenario_duration: i }); }}
                            minValue={1} maxValue={3600} />

                        {
                            (device.auto_refresh_timeout !== undefined) ?
                                <AskOnOff
                                    show={true}
                                    marginTop={16} header={ds.periodicRefresh} info={ds.periodicRefreshInfo}
                                    originalState={device.auto_refresh_timeout > 0 ? true : false}
                                    sendState={(s) => {
                                        device.changeState({ auto_refresh_timeout: s ? 180 : -1 });
                                    }} /> : null
                        }
                        {
                            ((device.auto_refresh_timeout !== undefined) && (device.auto_refresh_timeout > 0)) ?
                                <div className="inner-scope">
                                    <AskMinutes header={ds.durationBetweenRefreshes} marginTop={0}
                                        originalValue={device.auto_refresh_timeout}
                                        sendValue={(i) => { device.changeState({ auto_refresh_timeout: i }); }}
                                        minValue={1} maxValue={1500} />
                                </div> : null
                        }

                        <AskOnOff
                            show={true}
                            marginTop={24} header={ds.musicInScenario} info={ds.musicInScenarioInfo}
                            originalState={device.music_in_scenario ? true : false}
                            sendState={(s) => { device.changeState({ music_in_scenario: s }); }} />

                        {
                            ((device.lamp_key_function === 0) || (device.lamp_key_function === 1)) ?
                                <AskOnOff
                                    show={true}
                                    marginTop={24} header={ds.scenarioKey} info={ds.scenarioKeyInfo}
                                    originalState={device.lamp_key_function === 1 ? true : false}
                                    sendState={(s) => { device.changeState({ lamp_key_function: s ? 1 : 0 }); }} /> : null
                        }
                    </Fragment> : null
                }

                {/* Evaporative Cooler Settings */}
                {
                    ((device.type === DeviceType.evaporativeCooler) && (device.active)) ? <Fragment>

                        <div className="input-label">{ds.startingProcess}</div>

                        <EvaporativeCoolerSettingsChart device={device} />

                        <div className="inner-scope">
                            <AskSeconds header={ds.pumpDuration} marginTop={0}
                                originalValue={device.pump_only_duration}
                                sendValue={(i) => { device.changeState({ pump_only_duration: i }); }}
                                minValue={1} maxValue={900} />
                            <AskOnOff
                                show={true}
                                marginTop={16} header={ds.startWithFastMode} info={ds.startFastAfterPump}
                                originalState={device.start_with_fast_mode ? true : false}
                                sendState={(s) => { device.changeState({ start_with_fast_mode: s }); }} />
                            <AskOnOff
                                show={true}
                                marginTop={16} header={ds.keepFastMode} info={ds.fastModeAsFinalState}
                                originalState={(device.keep_fast_mode ? true : false) && (device.start_with_fast_mode ? true : false)}
                                sendState={(s) => { device.changeState({ keep_fast_mode: s }); }}
                                transparent={device.start_with_fast_mode ? false : true}
                                unclickable={device.start_with_fast_mode ? false : true} />
                        </div>
                    </Fragment> : null
                }

                {/* Relay Channel Names + keep_on_after_reboot */}
                {
                    device.type === DeviceType.relay4 ? (
                        <Fragment>
                            <AskOnOff
                                show={true}
                                marginTop={24} header={ds.channelNames} info={ds.channelNamesInfo}
                                originalState={device.use_custom_channel_names ? true : false}
                                sendState={(s) => { device.changeState({ use_custom_channel_names: s }); }} />

                            {device.use_custom_channel_names ? <Fragment>
                                <AskText header={ds.channelName1}
                                    originalText={device.channel1_name !== undefined ?
                                        device.channel1_name : ds.channelName1Default}
                                    sendText={(s) => { device.changeState({ channel1_name: s }); }} />

                                <AskText header={ds.channelName2}
                                    originalText={device.channel2_name !== undefined ?
                                        device.channel2_name : ds.channelName2Default}
                                    sendText={(s) => { device.changeState({ channel2_name: s }); }} />

                                <AskText header={ds.channelName3}
                                    originalText={device.channel3_name !== undefined ?
                                        device.channel3_name : ds.channelName3Default}
                                    sendText={(s) => { device.changeState({ channel3_name: s }); }} />


                                <AskText header={ds.channelName4}
                                    originalText={device.channel4_name !== undefined ?
                                        device.channel4_name : ds.channelName4Default}
                                    sendText={(s) => { device.changeState({ channel4_name: s }); }} />
                            </Fragment> : null}

                            <AskOnOff
                                show={(device.keep_on_after_reboot !== undefined) && (device.active)}
                                marginTop={16} header={ds.keepOnAfterRebootH} info={ds.keepOnAfterReboot}
                                originalState={device.keep_on_after_reboot ? true : false}
                                sendState={(s) => { device.changeState({ keep_on_after_reboot: s }); }} />
                        </Fragment>
                    ) : null
                }

                {/* Sync Devices */}
                {isSyncSettingsSupported ? <Fragment>
                    <div className="input-label">{ds.syncSettings.header}</div>
                    <div className="show-name-container">
                        <div className="label">{ds.syncSettings.info[syncSettingsMode]}</div>
                        <div />
                        <div className="border" onClick={() => hs.push("/devices/detail/sync_settings", { mac: device.mac, connected: connected })}>
                            <div className="label">{ds.edit}</div>
                            <IconSettingsGears className="icon" />
                        </div>
                    </div>
                </Fragment> : null}

                {/* Colors */}
                {
                    showColorSettings ? (
                        canUseRoomColor ?
                            <Fragment>
                                <AskOnOff
                                    show={true}
                                    marginTop={24} marginBottom={device.use_room_color ? 0 : 32 - 5}
                                    header={ds.color} info={ds.useRoomColor}
                                    originalState={device.use_room_color ? true : false}
                                    sendState={(s) => { device.changeState({ use_room_color: s }); }} />

                                {device.use_room_color ? null :
                                    <ColorSelector
                                        selectedColorCode={selectedColorCode}
                                        setColorCode={setColorCode} />}
                            </Fragment>
                            :
                            <Fragment>
                                <div className="color-header">{ds.color}</div>
                                <ColorSelector
                                    selectedColorCode={selectedColorCode}
                                    setColorCode={setColorCode} />
                            </Fragment>
                    ) : null
                }

                {/* OTA */}
                <OTASection
                    showVersionInfo={showVersionInfo} deviceCanUpdate={deviceCanUpdate}
                    ds={ds} startOTA={startOTA}
                    deviceVersion={formatDeviceVersion(device)} />
            </div>
        </div>
    );
}

function formatDeviceVersion({ version_name, model }) {
    if (model !== undefined) {
        if (version_name !== undefined) {
            return version_name + " " + model
        } else {
            return model
        }
    } else {
        if (version_name !== undefined) {
            return version_name
        }
    }
    return undefined;
}

function OTASection({ ds, showVersionInfo, startOTA, deviceCanUpdate, deviceVersion }) {
    // TODO:
    // پیام مربوط به
    // deviceCanUpdate
    // رو بهتر کنیم
    // چون ممکنه دستگاه ورژن جدید داشته باشه ولی پروتکل او‌تو‌ای‌ رو ساپورت نکنه مثلا در حالت سولو یا یه گیتوی قدیمی

    return (
        !showVersionInfo ? undefined : <Fragment>
            <div className="version-title">
                <div className="title">{ds.firmwareVersion}</div>
                {
                    (deviceVersion !== undefined) ? <div className="version">({deviceVersion})</div> : undefined
                }
            </div>
            <div className="version-info">
                {
                    deviceCanUpdate ?
                        <Fragment>
                            <div className="hot-label">{ds.firmwareNewVersion}</div>
                            <div className="button-border" onClick={() => startOTA()}>
                                <div className="label">{ds.firmwareTapToUpdate}</div>
                                <IconDownload className="icon" />
                            </div>
                        </Fragment> :
                        <div className="label">{ds.firmwareIsLatest}</div>
                }
            </div>
        </Fragment>
    )
}


export function AudioControllerManagePage({ devices, otaProgressController, currentUser }) {
    const s = useLocalizedStrings();
    const ds = s.deviceSettings;

    const p = useContext(CurrentPageContext);
    const dismiss = p.dismiss;

    const device = useMemo(() => {
        return devices.find(({ mac }) => mac === p.state.mac);
    }, [p, devices]);
    const [deviceCache, setDeviceCache] = useState(device);
    useEffect(() => {
        if (device === undefined) {
            dismiss();
        } else {
            setDeviceCache(device);
        }
    }, [dismiss, device]);

    const connected = (deviceCache.active === true);
    const isOn = (deviceCache.is_on === true);

    const showVersionInfo =
        (deviceCache.ota_exists !== undefined) &&
        (deviceCache.ota_exists !== null) &&
        (currentUser.permissions.setting) && connected;
    const deviceCanUpdate = deviceCache.ota_exists === true;
    const startOTA = useOTAReportPage(otaProgressController, OTAType.device, deviceCache.startOTA);

    const audioControllerModeButtons = useMemo(() => {
        return [
            {
                key: "audio-contl-modeHinava",
                className: (deviceCache.mode === AmplifierMode.hinava) ? "button turquoise" : "button black",
                onClick: () => { if (deviceCache.mode !== AmplifierMode.hinava) deviceCache.changeState({ mode: AmplifierMode.hinava }) },
                icon: (deviceCache.mode === AmplifierMode.hinava) ? IconModeHinavaDark : IconModeHinava,
                label: s.audioController.modeHinava,
            },
            {
                key: "audio-contl-modeBluetooth",
                className: (deviceCache.mode === AmplifierMode.ble) ? "button turquoise" : "button black",
                onClick: () => { if (deviceCache.mode !== AmplifierMode.ble) deviceCache.changeState({ mode: AmplifierMode.ble }) },
                icon: (deviceCache.mode === AmplifierMode.ble) ? IconModeBleDark : IconModeBle,
                label: s.audioController.modeBluetooth,
            },
            {
                key: "audio-contl-modeAUX",
                className: (deviceCache.mode === AmplifierMode.aux) ? "button turquoise" : "button black",
                onClick: () => { if (deviceCache.mode !== AmplifierMode.aux) deviceCache.changeState({ mode: AmplifierMode.aux }) },
                icon: (deviceCache.mode === AmplifierMode.aux) ? IconModeAuxDark : IconModeAux,
                label: s.audioController.modeAUX,
            },
            {
                key: "audio-contl-modeHDMI",
                className: (deviceCache.mode === AmplifierMode.hdmi) ? "button turquoise" : "button black",
                onClick: () => { if (deviceCache.mode !== AmplifierMode.hdmi) deviceCache.changeState({ mode: AmplifierMode.hdmi }) },
                icon: (deviceCache.mode === AmplifierMode.hdmi) ? IconModeHdmiDark : IconModeHdmi,
                label: s.audioController.modeHDMI,
            },
        ]
    }, [deviceCache, s.audioController]);

    return (
        <div className="device-detail-page">
            <div className="bg-blank" />
            <div className={connected ? "bg-secondary-color" : "bg-disconnect-color"} />
            <div className="add-device-page-content device-setting-page-content">
                <CustomIconBack className="nav-icon" /> <div className="nav-button" onClick={dismiss} />

                <div className="header">{s.audioController.manage}</div>

                {/* NAME */}
                <AskText
                    header={ds.name} originalText={deviceCache.name}
                    sendText={deviceCache.rename} canNotEdit={currentUser.permissions.setting !== true} />

                {/* Audio Controller */}
                {
                    (connected && isOn) ? (
                        <Fragment>
                            <div className="input-label t24">{s.audioController.mode}</div>
                            <div className="audio-controller-mode-buttons">
                                {
                                    audioControllerModeButtons.map(button => (
                                        <div
                                            key={button.key}
                                            className={button.className}
                                            onClick={button.onClick}>
                                            {
                                                React.createElement(button.icon, { className: "icon" })
                                            }
                                            <div className="label">{button.label}</div>
                                        </div>
                                    ))
                                }
                            </div>
                        </Fragment>
                    ) : null
                }

                <AudioControllerZones device={deviceCache} canEditNames={currentUser.permissions.setting} />

                {/* OTA */}
                <OTASection
                    showVersionInfo={showVersionInfo} deviceCanUpdate={deviceCanUpdate}
                    ds={ds} startOTA={startOTA}
                    deviceVersion={formatDeviceVersion(device)} />
            </div>
        </div>
    );
}

export function AudioControllerZones({ device, canEditNames }) {
    const s = useLocalizedStrings();

    const zoneNames = useMemo(() => {
        return [
            ((device.zone1_name !== "") && (device.zone1_name !== undefined)) ? device.zone1_name : s.audioController.zone1,
            ((device.zone2_name !== "") && (device.zone2_name !== undefined)) ? device.zone2_name : s.audioController.zone2,
            ((device.zone3_name !== "") && (device.zone3_name !== undefined)) ? device.zone3_name : s.audioController.zone3,
            ((device.zone4_name !== "") && (device.zone4_name !== undefined)) ? device.zone4_name : s.audioController.zone4,
        ]
    }, [device.zone1_name, device.zone2_name, device.zone3_name, device.zone4_name, s.audioController]);

    const [isEditActive, _setEditActive] = useState(false);
    const [tempNames, _setTempNames] = useState(zoneNames);

    const setEditActive = useCallback(() => {
        _setTempNames(zoneNames);
        _setEditActive(true);
    }, [zoneNames]);

    const setTempNameFor = useCallback((i, newName) => {
        _setTempNames(names => {
            let ns = [...names];
            ns[i] = newName;
            return ns;
        })
    }, []);

    const saveNames = useCallback(() => {
        let obj = {};
        obj["zone1_name"] = tempNames[0];
        obj["zone2_name"] = tempNames[1];
        obj["zone3_name"] = tempNames[2];
        obj["zone4_name"] = tempNames[3];
        // for (let i = 0; i < 4; i++) {
        //     if (tempNames[i].length > 0) {
        //         obj["zone" + (i + 1).toString() + "_name"] = tempNames[i];
        //     }
        // }
        device.changeState(obj);
        _setEditActive(false);
    }, [device, tempNames]);

    const canChangeState = (device.active === true) && (device.is_on === true);

    if (!isEditActive) {
        return (
            <Fragment>
                <div className="audio-controller-zones-header">
                    <div className="input-label">{s.audioController.zones}</div>
                    {canEditNames ?
                        <div className="button-border" onClick={setEditActive}>
                            <div className="label">{s.audioController.zoneEditNames}</div>
                            <IconEdit className="icon" />
                        </div> : null}
                </div>

                <div className="audio-controller-zone-buttons">
                    <div className="on-off">
                        <div className="text">{zoneNames[0]}</div>
                        {canChangeState ?
                            <Toggle enable={device.zone1} onClick={() => { device.changeState({ zone1: !device.zone1 }) }}
                                customHeadIconType={ToggleHeadIconType.checkOrX} />
                            : null}
                    </div>
                    <div className="on-off">
                        <div className="text">{zoneNames[1]}</div>
                        {canChangeState ?
                            <Toggle enable={device.zone2} onClick={() => { device.changeState({ zone2: !device.zone2 }) }}
                                customHeadIconType={ToggleHeadIconType.checkOrX} />
                            : null}
                    </div>
                    <div className="on-off">
                        <div className="text">{zoneNames[2]}</div>
                        {canChangeState ?
                            <Toggle enable={device.zone3} onClick={() => { device.changeState({ zone3: !device.zone3 }) }}
                                customHeadIconType={ToggleHeadIconType.checkOrX} />
                            : null}
                    </div>
                    <div className="on-off">
                        <div className="text">{zoneNames[3]}</div>
                        {canChangeState ?
                            <Toggle enable={device.zone4} onClick={() => { device.changeState({ zone4: !device.zone4 }) }}
                                customHeadIconType={ToggleHeadIconType.checkOrX} />
                            : null}
                    </div>
                </div>
            </Fragment>
        )
    } else {
        return (
            <Fragment>
                <div className="audio-controller-zones-header">
                    <div className="input-label">{s.audioController.zones}</div>
                    <div className="button-border save" onClick={saveNames}>
                        <div className="label">{s.audioController.zoneSaveNames}</div>
                        <IconTick className="icon" />
                    </div>
                </div>

                <div className="audio-controller-zone-edit-texts">
                    <input className="input-box" placeholder={zoneNames[0]}
                        value={tempNames[0]} onChange={e => setTempNameFor(0, e.target.value)}></input>
                    <input className="input-box" placeholder={zoneNames[1]}
                        value={tempNames[1]} onChange={e => setTempNameFor(1, e.target.value)}></input>
                    <input className="input-box" placeholder={zoneNames[2]}
                        value={tempNames[2]} onChange={e => setTempNameFor(2, e.target.value)}></input>
                    <input className="input-box" placeholder={zoneNames[3]}
                        value={tempNames[3]} onChange={e => setTempNameFor(3, e.target.value)}></input>
                </div>
            </Fragment>
        )
    }
}

function AskOnOff({ show, marginTop, marginBottom, header, info, originalState, sendState, transparent, unclickable }) {
    const [_on, _setOn] = useState(originalState);
    useEffect(() => {
        _setOn(originalState);
    }, [originalState]);
    const onClick = useCallback(() => {
        sendState(!_on);
        _setOn(!_on);
    }, [_on, sendState]);

    return (
        show ?
            <div className="ask-on-off" style={{ marginTop: marginTop, marginBottom: marginBottom }}>
                <div className="label">{header}</div>
                <div className="info">{info}</div>
                <Toggle onClick={onClick} enable={_on} customHeadIconType={ToggleHeadIconType.checkOrX}
                    transparent={transparent} unclickable={unclickable} />
            </div> : null
    )
}

export function AskText({ header, originalText, sendText, canNotEdit, marginTop }) {
    const s = useLocalizedStrings();
    const ds = s.deviceSettings;

    const [isEditActive, _setEditActive] = useState(false);
    const [tempText, setTempText] = useState(originalText);

    const setEditActive = useCallback(() => {
        setTempText(originalText);
        _setEditActive(true);
    }, [originalText]);
    const updateText = useCallback(() => {
        if (tempText.length > 1) {
            sendText(tempText);
            _setEditActive(false);
        }
    }, [sendText, tempText]);

    return (
        <Fragment>
            <div className="input-label" style={{ marginTop }}>{header}</div>

            {isEditActive ? (
                <div className="input-box-container">
                    <input className="input-box" placeholder={originalText}
                        value={tempText} onChange={e => setTempText(e.target.value)}></input>
                    <div className="button" onClick={updateText}>{ds.save}</div>
                </div>
            ) : (
                <div className="show-name-container">
                    <div className="label">{originalText}</div>
                    <div />
                    {(canNotEdit === true) ?
                        null :
                        <div className="border" onClick={setEditActive}>
                            <div className="label" onClick={setEditActive}>{ds.edit}</div>
                            <IconEdit className="icon" onClick={setEditActive} />
                        </div>}
                </div>
            )}
        </Fragment>
    )
}

// eslint-disable-next-line no-unused-vars
function AskMilliSeconds({ header, originalValue, sendValue, minValue, maxValue, marginTop }) {
    const l = useContext(LocalizationContext);
    const s = l.strings;
    const ds = s.deviceSettings;

    return AskNamedNumber({
        header, originalValue, sendValue, minValue, maxValue, marginTop,
        singularValuePostfix: ds.millisecond,
        pluralValuePostfix: ds.milliseconds,
    })
}

function AskSeconds({ header, originalValue, sendValue, minValue, maxValue, marginTop, supportDot }) {
    const l = useContext(LocalizationContext);
    const s = l.strings;
    const ds = s.deviceSettings;

    return AskNamedNumber({
        header, originalValue, sendValue, minValue, maxValue, marginTop, supportDot,
        singularValuePostfix: ds.second,
        pluralValuePostfix: ds.seconds,
    })
}

function AskMinutes({ header, originalValue, sendValue, minValue, maxValue, marginTop }) {
    const l = useContext(LocalizationContext);
    const s = l.strings;
    const ds = s.deviceSettings;

    return AskNamedNumber({
        header, originalValue, sendValue, minValue, maxValue, marginTop,
        singularValuePostfix: ds.minute,
        pluralValuePostfix: ds.minutes,
    })
}

function AskDegree({ header, originalValue, sendValue, minValue, maxValue, marginTop, supportNegativeNumber, supportDot }) {
    const l = useContext(LocalizationContext);
    const s = l.strings;
    const ds = s.deviceSettings;

    return AskNamedNumber({
        header, originalValue, sendValue, minValue, maxValue, marginTop,
        supportNegativeNumber, supportDot,
        singularValuePostfix: ds.degree,
        pluralValuePostfix: ds.degrees,
    })
}

function AskPercentage({ header, originalValue, sendValue, minValue, maxValue, marginTop }) {
    const l = useContext(LocalizationContext);
    const s = l.strings;
    const ds = s.deviceSettings;

    return AskNamedNumber({
        header, originalValue, sendValue, minValue, maxValue, marginTop,
        singularValuePostfix: ds.percent,
        pluralValuePostfix: ds.percent,
    })
}
function AskNamedNumber({
    header, originalValue, sendValue,
    minValue, maxValue, supportNegativeNumber, supportDot,
    marginTop,
    singularValuePostfix, pluralValuePostfix }) {
    const l = useContext(LocalizationContext);
    const s = l.strings;
    const ds = s.deviceSettings;

    const [isEditActive, _setEditActive] = useState(false);
    const [tempValue, _setTempValue] = useState(originalValue);

    const setEditActive = useCallback(() => {
        _setTempValue(originalValue);
        _setEditActive(true);
    }, [originalValue]);
    const updateValue = useCallback(() => {
        if (tempValue !== undefined) {
            let value;
            if (supportDot) {
                value = parseFloat(tempValue);
            } else {
                value = parseInt(tempValue);
            }
            sendValue(Math.min(Math.max(value, minValue), maxValue));
            _setEditActive(false);
        } else {
            // sendValue(originalValue);
            _setEditActive(false);
        }
    }, [sendValue, tempValue, minValue, maxValue, supportDot]);
    const setTempValue = useCallback((v) => {
        if ((v.length === 0) || (toEnglishNumber(v, false, (minValue < 0)).length === 0)) {
            _setTempValue(undefined);
        } else {
            const d = toEnglishNumber(v, false, (minValue < 0), supportDot);
            _setTempValue(d);
        }
    }, [minValue, supportDot]);

    const formatValueToShow = useCallback((t) => {
        if (t === undefined) {
            return "";
        }
        if ((-1 <= t) && (t <= 1)) {
            return l.localizeAndFixSignedNumbers(t) + " " + singularValuePostfix;
        } else {
            return l.localizeAndFixSignedNumbers(t) + " " + pluralValuePostfix;
        }
    }, [l, singularValuePostfix, pluralValuePostfix]);
    const formatValueToEdit = useCallback((t) => {
        if (t === undefined) {
            return "";
        }
        return l.localizeNumber(t);
    }, [l]);

    return (
        <Fragment>
            <div className="input-label" style={{ marginTop }}>{header}</div>

            {isEditActive ? (
                <div className="input-box-container">
                    <input className={"input-box" + (supportNegativeNumber ? " support-negative-number" : "")}
                        placeholder={formatValueToEdit(originalValue)}
                        value={formatValueToEdit(tempValue)}
                        onChange={e => { setTempValue(e.target.value) }}></input>
                    <div className="button" onClick={updateValue}>{ds.save}</div>
                </div>
            ) : (
                <div className="show-name-container">
                    <div className="label">{formatValueToShow(originalValue)}</div>
                    <div />
                    <div className="border" onClick={setEditActive}>
                        <div className="label">{ds.edit}</div>
                        <IconEdit className="icon" />
                    </div>
                </div>
            )}
        </Fragment>
    )
}

export function ColorSelector({ selectedColorCode, setColorCode }) {
    const s = useLocalizedStrings();
    const ds = s.deviceSettings;

    return (
        <div className="colors-selector">
            {
                DeviceColors.map(item => {
                    if (selectedColorCode === item[0]) {
                        return <div className="color-item" key={`device-setting-color-${item}`}>
                            <div className="circle active" style={{ backgroundColor: item[1] }}>
                                {item[0] === 11 ? "OFF" : undefined}
                            </div>
                            <div className="name">{ds.colors[item[0]]}</div>
                        </div>;
                    } else {
                        return <div className="color-item" key={`device-setting-color-${item}`}>
                            <div className="circle" style={{ backgroundColor: item[1] }}
                                onClick={() => {
                                    setColorCode(item[0])
                                }}>
                                {item[0] === 11 ? "OFF" : undefined}
                            </div>
                        </div>;
                    }
                })
            }
        </div>
    );
}

export function useRemoveDevicePopup(deviceCache, onBackClick) {
    const s = useLocalizedStrings();

    const [popupId, showPopup, dismissPopup] = usePopup();

    const removeDevicePopup = useMemo(() => {
        return (
            <Popup popupId={popupId} name="clear_data_popup" baseZIndex={12}>
                <div className="clear-data-popup">
                    <div className="header">{s.removeDeviceConfirmAreYouSure}</div>
                    <div className="buttons-group">
                        <div className="delete-button" onClick={() => {
                            dismissPopup();
                            setTimeout(() => {
                                onBackClick();
                                deviceCache.remove();
                            });
                        }}>
                            <div className="label">{s.removeDeviceConfirmOK}</div>
                            <IconDeleteRed className="icon" />
                        </div>
                        <div className="dismiss-button" onClick={dismissPopup}>{s.removeDeviceConfirmNO}</div>
                    </div>
                </div>
            </Popup>
        );
    }, [deviceCache, dismissPopup, popupId, onBackClick,
        s.removeDeviceConfirmAreYouSure, s.removeDeviceConfirmNO, s.removeDeviceConfirmOK]);

    return [showPopup, removeDevicePopup];
}

export default DeviceDetail;